import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, X } from 'lucide-react';
import {
  clearPendingToolDraft,
  getPendingToolDraft,
  loadToolDraft,
  type PendingToolDraft,
} from '../../lib/toolState';
import { TOOL_EVENTS, track } from '../../lib/analytics';

/**
 * Shown on the dashboard after a visitor saves a tool draft and signs up.
 * This is the "your work is still here" moment that the old flow was missing.
 */
export function ToolDraftResumeCard() {
  const [pending, setPending] = useState<PendingToolDraft | null>(() => getPendingToolDraft());

  if (!pending) return null;

  const draft = loadToolDraft(pending.toolSlug);
  const savedAt = draft?.savedAt || pending.timestamp;
  const savedLabel = savedAt
    ? new Date(savedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'recently';

  const handleDismiss = () => {
    clearPendingToolDraft();
    setPending(null);
    track(TOOL_EVENTS.resumeClick, {
      tool_slug: pending.toolSlug,
      action: 'dismiss',
    });
  };

  return (
    <section className="relative border-[4px] border-black dark:border-white bg-[#0044FF] text-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] p-5 md:p-6">
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 border-2 border-white/40 hover:border-white hover:bg-white/10 transition-colors cursor-pointer"
        aria-label="Dismiss saved tool draft"
      >
        <X size={16} strokeWidth={3} />
      </button>

      <div className="flex flex-col md:flex-row md:items-center gap-5 pr-8">
        <div className="w-12 h-12 shrink-0 bg-[#FFD600] text-black border-[3px] border-black flex items-center justify-center shadow-[3px_3px_0px_#000]">
          <FileText size={22} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono font-black uppercase tracking-widest text-[#FFD600]">
            Your work is still here
          </p>
          <h2 className="text-2xl md:text-3xl font-black font-display uppercase tracking-tighter leading-none mt-1">
            Continue your {pending.toolTitle}
          </h2>
          <p className="text-xs md:text-sm font-bold text-white/85 mt-2 max-w-2xl leading-relaxed">
            {pending.orgName ? `${pending.orgName} · ` : ''}
            Saved {savedLabel}. Your inputs are waiting on this device — pick up exactly where you left off.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 shrink-0">
          <Link
            to={`/tools/${pending.toolSlug}?resume=1`}
            onClick={() =>
              track(TOOL_EVENTS.resumeClick, {
                tool_slug: pending.toolSlug,
                action: 'continue',
              })
            }
            className="bg-[#FFD600] text-black border-[3px] border-black font-black uppercase tracking-wider px-5 py-3 text-xs hover:bg-[#E6C200] transition-colors shadow-[4px_4px_0px_#000] flex items-center justify-center gap-2"
          >
            Continue Draft
            <ArrowRight size={15} strokeWidth={3} />
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            className="bg-transparent text-white border-[3px] border-white/50 font-black uppercase tracking-wider px-5 py-3 text-xs hover:bg-white/10 transition-colors cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      </div>
    </section>
  );
}
